import { Trans, useLingui } from '@lingui/react/macro'
import { EdgeFade } from 'designSystem/components/EdgeFade'
import { Text } from 'designSystem/components/Text'
import { useMediaQuery } from 'designSystem/components/useMediaQuery'
import { useAdvancedSearchUX } from 'features/knowledgeDocumentViewer/components/advancedSearch/contexts/AdvancedSearchUXContext'
import type { PreviewTrendHandler } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/trendPreview'
import type { DocChatTurn } from 'features/knowledgeDocumentViewer/types'
import { type KeyboardEvent, type TransitionEvent, useState } from 'react'

import {
  ChatColumn,
  ChatPage,
  ComposerStage,
  ConversationStage,
  LandingSectionsScroll,
  LandingSectionsStage,
  LogoStage,
} from './agenticChatPageStyles'
import { CantileverLandingLogo } from './CantileverLandingLogo'
import { DocChatMessageQueue } from './DocChatMessageQueue'
import { DocChatSearchBox } from './DocChatSearchBox'
import { DocChatTimeline } from './DocChatTimeline'
import { LandingRecentsAndFavorites } from './LandingRecentsAndFavorites'
import { isLandingSectionsExitComplete } from './landingSectionsExit'
import type {
  NavigateDocumentHandler,
  PreviewDocumentHandler,
  PreviewImageHandler,
  PreviewInspectionHandler,
  PreviewRevisionHandler,
} from './previewDocumentHandler'

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)'

interface AgenticChatColumnProps {
  hasConversation: boolean
  onPreviewDocument: PreviewDocumentHandler
  onPreviewTrend?: PreviewTrendHandler
  onPreviewImage?: PreviewImageHandler
  onPreviewInspection?: PreviewInspectionHandler
  onNavigateDocument?: NavigateDocumentHandler
  onPreviewRevision?: PreviewRevisionHandler

  onSearchKeyDown: (event: KeyboardEvent) => void
  onSearchSubmit: () => void
  onStop: () => void
  isGenerating: boolean
  canSubmit: boolean
  conversationId?: string | null

  turns: DocChatTurn[]
}

export function AgenticChatColumn({
  hasConversation,
  onPreviewDocument,
  onPreviewTrend,
  onPreviewImage,
  onPreviewInspection,
  onNavigateDocument,
  onPreviewRevision,

  onSearchKeyDown,
  onSearchSubmit,
  onStop,
  isGenerating,
  canSubmit,
  conversationId,

  turns,
}: AgenticChatColumnProps) {
  const {
    favoriteSearches,
    handleSearchInputChange,

    isSearchFavorited,
    onClearSearchHistory,
    onSuggestionSelect,
    searchInput,
    toggleSearchFavorite,
  } = useAdvancedSearchUX()
  const { t } = useLingui()
  const prefersReducedMotion = useMediaQuery(REDUCED_MOTION_QUERY)
  const [showLandingSections, setShowLandingSections] =
    useState(!hasConversation)
  if (!hasConversation && !showLandingSections) {
    setShowLandingSections(true)
  }

  const handleLandingSectionsTransitionEnd = (
    event: TransitionEvent<HTMLDivElement>
  ) => {
    if (
      isLandingSectionsExitComplete({
        hasConversation,
        isOwnTransition: event.target === event.currentTarget,
        prefersReducedMotion,
        propertyName: event.propertyName,
      })
    ) {
      setShowLandingSections(false)
    }
  }

  return (
    <ChatPage>
      <ChatColumn
        $hasConversation={hasConversation}
        data-state={hasConversation ? 'active' : 'landing'}>
        <LogoStage $hasConversation={hasConversation} aria-hidden>
          <CantileverLandingLogo />
        </LogoStage>
        <ConversationStage
          $hasConversation={hasConversation}
          aria-hidden={!hasConversation}>
          <DocChatTimeline
            turns={turns}
            variant='sidePanel'
            conversationId={conversationId}
            onPreviewDocument={onPreviewDocument}
            onPreviewTrend={onPreviewTrend}
            onPreviewImage={onPreviewImage}
            onPreviewInspection={onPreviewInspection}
            onNavigateDocument={onNavigateDocument}
            onPreviewRevision={onPreviewRevision}
          />
          <EdgeFade placement='top' size='sm' />
          <EdgeFade placement='bottom' size='sm' />
        </ConversationStage>
        <ComposerStage $hasConversation={hasConversation}>
          <DocChatMessageQueue />
          <DocChatSearchBox
            searchInput={searchInput}
            onSearchChange={handleSearchInputChange}
            onSearchKeyDown={onSearchKeyDown}
            onSearchSubmit={onSearchSubmit}
            placeholder={t`Ask Cantilever...`}
            submitLabel={t`Ask Cantilever`}
            onSuggestionSelect={onSuggestionSelect}
            onClearHistory={onClearSearchHistory}
            favoriteSearches={favoriteSearches}
            isSearchFavorited={isSearchFavorited}
            onToggleSearchFavorite={toggleSearchFavorite}
            contentFilter='documents'
            requireQuery
            submitDisabled={!canSubmit}
            isGenerating={isGenerating}
            onStop={onStop}
          />
          {hasConversation ? (
            <Text as='p' variant='caption' color='muted' align='center'>
              <Trans>
                Cantilever can make mistakes. Double-check responses.
              </Trans>
            </Text>
          ) : null}
        </ComposerStage>
        {showLandingSections ? (
          <LandingSectionsStage
            $exiting={hasConversation}
            aria-hidden={hasConversation || undefined}
            onTransitionEnd={handleLandingSectionsTransitionEnd}>
            <LandingSectionsScroll>
              <LandingRecentsAndFavorites />
            </LandingSectionsScroll>
            <EdgeFade placement='top' />
            <EdgeFade placement='bottom' />
          </LandingSectionsStage>
        ) : null}
      </ChatColumn>
    </ChatPage>
  )
}
